import styled, { css, keyframes } from 'styled-components';
import { media } from '../../../styles/theme';

// Grid geometry — shared by Room View and Teacher View so both scroll in step
// Default window. GridView widens it when the week actually contains something outside it,
// so a class at 07:00 or 22:30 is drawn rather than silently clipped off the grid.
export const TIME_START = 8;   // 08:00
export const TIME_END = 22;    // 22:00
export const DEFAULT_TRACK_HOURS = TIME_END - TIME_START;
export const HOUR_HEIGHT = 56; // px per hour, time-vertical layout
export const HOUR_WIDTH = 96;  // px per hour, time-horizontal layout
export const LANE_HEIGHT = 62; // px per room/teacher row, time-horizontal layout
export const LANE_LABEL_WIDTH = 132;
export const MIN_BLOCK_PX = 22;

export const BRAND = '#F1AF59';

export const Container = styled.div`
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;

  ${media.phone} {
    padding: 12px;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const Title = styled.h1`
  font-size: 24px;

  ${media.phone} {
    font-size: 19px;
  }
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  /* On a phone the toolbar is most of the screen if it wraps to four rows. Each group keeps
     its own line and scrolls sideways instead, so the grid still starts above the fold. */
  ${media.phone} {
    gap: 8px;
    margin-bottom: 10px;
  }
`;

export const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ToolbarLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const ViewSwitcher = styled.div`
  display: inline-flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;

  ${media.phone} {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar { display: none; }
  }
`;

export const ViewButton = styled.button`
  padding: 8px 16px;
  white-space: nowrap;
  border: none;
  border-radius: 6px;
  background: ${props => (props.$active ? 'white' : 'transparent')};
  color: ${props => (props.$active ? '#1f2937' : '#6b7280')};
  font-size: 13px;
  font-weight: ${props => (props.$active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: ${props => (props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none')};

  &:hover {
    color: #1f2937;
  }
`;

export const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  max-width: 190px;

  &:focus {
    outline: none;
    border-color: ${BRAND};
  }
`;

export const NavButton = styled.button`
  padding: 8px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: ${BRAND};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled.button`
  padding: 9px 18px;
  border: none;
  border-radius: 6px;
  background: ${BRAND};
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #e09b3d;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DateDisplay = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  min-width: 210px;
  text-align: center;
`;

export const Panel = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

export const DayTabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

export const DayTab = styled.button`
  padding: 8px 14px;
  border: ${props => (props.$isDropTarget ? '2px dashed' : '1px solid')} ${props => (props.$active || props.$isDropTarget ? BRAND : '#e5e7eb')};
  border-radius: 8px;
  background: ${props => (props.$active ? BRAND : props.$isDropTarget ? '#fff7ed' : 'white')};
  color: ${props => (props.$active ? 'white' : '#374151')};
  font-size: 13px;
  font-weight: ${props => (props.$active ? 600 : 500)};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;

  &:hover {
    border-color: ${BRAND};
  }
`;

export const EmptyState = styled.div`
  padding: 64px 24px;
  text-align: center;
  color: #9ca3af;

  h3 {
    margin: 12px 0 6px;
    font-size: 16px;
    color: #4b5563;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
`;

export const LoadingState = styled.div`
  padding: 64px 24px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

/**
 * Wraps the grid while a refresh is in flight.
 *
 * The week already on screen stays mounted — same cards, same scroll position — and is dimmed
 * and made inert rather than being replaced by a loading panel. Swapping it out unmounts every
 * card, so moving a single class by 15 minutes used to look like a full page reload.
 */
export const RefreshLayer = styled.div`
  transition: filter 160ms ease, opacity 160ms ease;

  ${({ $busy }) => $busy && css`
    filter: blur(1.4px) saturate(0.85);
    opacity: 0.55;
    pointer-events: none;
    user-select: none;
  `}
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`;

/** Sits above the dimmed grid so "blurred" reads as "working", not as "broken". */
export const RefreshBadge = styled.div`
  position: sticky;
  top: 8px;
  z-index: 30;
  width: fit-content;
  margin: 0 auto -28px;
  padding: 5px 14px;
  border-radius: 999px;
  background: #111827;
  color: white;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  animation: ${pulse} 1.1s ease-in-out infinite;
  pointer-events: none;
`;

export const ErrorState = styled.div`
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #b91c1c;
  font-size: 14px;
  margin-bottom: 16px;
`;

export const StatsBar = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  font-size: 13px;
  color: #6b7280;
`;

export const Stat = styled.span`
  strong {
    color: #1f2937;
    font-weight: 600;
  }
`;

// ==================== Grid (Room / Teacher views) ====================

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 64px repeat(${props => props.$columns || 1}, minmax(150px, 1fr));
  min-height: ${props => (props.$hours || DEFAULT_TRACK_HOURS) * HOUR_HEIGHT + 48}px;

  /* Narrower columns and a slimmer time gutter fit three of them on a phone instead of one
     and a half. The hour height is deliberately untouched: GridView positions every card
     from the same constant in JavaScript, so changing it in CSS alone would detach the
     cards from the hour lines they sit on. */
  ${media.phone} {
    grid-template-columns: 44px repeat(${props => props.$columns || 1}, minmax(112px, 1fr));
  }
`;

export const GridHeaderCell = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  background: ${BRAND};
  color: white;
  padding: 10px 8px;
  text-align: center;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid #e5e7eb;

  ${media.phone} {
    padding: 7px 4px;
    font-size: 11px;
  }

  span {
    display: block;
    font-weight: 400;
    font-size: 11px;
    opacity: 0.85;
  }
`;

export const TimeHeaderCell = styled(GridHeaderCell)`
  background: #f9fafb;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  z-index: 4;
`;

export const TimeColumn = styled.div`
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
`;

export const TimeCell = styled.div`
  height: ${HOUR_HEIGHT}px;
  padding: 2px 8px;
  font-size: 11px;

  ${media.phone} {
    padding: 2px 4px;
    font-size: 10px;
  }
  color: #9ca3af;
  text-align: right;
  border-bottom: 1px solid #f3f4f6;
`;

export const GridColumn = styled.div`
  position: relative;
  border-right: 1px solid #e5e7eb;
  min-height: ${props => (props.$hours || DEFAULT_TRACK_HOURS) * HOUR_HEIGHT}px;
  cursor: ${props => (props.$creatable ? 'cell' : 'default')};
  background: ${props => (props.$isDropTarget ? 'rgba(241, 175, 89, 0.12)' : 'transparent')};
  transition: background 0.1s;

  &:last-child {
    border-right: none;
  }
`;

export const HourLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: ${props => props.$top}px;
  border-top: 1px solid #f3f4f6;
  pointer-events: none;
`;

/**
 * A class card. Every edge is driven by a prop because the same component is positioned by
 * both orientations: time-vertical sets top/height from the clock and left/width from the
 * overlap lane, time-horizontal does exactly the opposite. `GridView`'s axis descriptor
 * decides which is which, so all four arrive here already resolved.
 */
export const Block = styled.div`
  position: absolute;
  left: ${props => props.$left ?? '2px'};
  top: ${props => props.$top ?? '2px'};
  height: ${props => props.$height ?? 'calc(100% - 4px)'};
  width: ${props => props.$width ?? 'calc(100% - 4px)'};
  background: ${props => props.$color};
  border-radius: 6px;
  padding: 5px 7px;
  color: white;
  font-size: 11px;
  line-height: 1.35;
  overflow: hidden;
  cursor: ${props => (props.$draggable ? 'grab' : 'pointer')};
  z-index: ${props => props.$zIndex || 1};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transition: transform 0.1s, box-shadow 0.1s;
  touch-action: none;
  user-select: none;
  visibility: ${props => (props.$dragging ? 'hidden' : 'visible')};

  /* A teacher's class at another campus: hatched grey, no details, not interactive. It
     exists so the teacher does not look free when they are not. */
  ${props => props.$busy && `
    background: repeating-linear-gradient(135deg, #9ca3af 0 6px, #b8bcc4 6px 12px);
    color: #1f2937;
    cursor: default;
    box-shadow: none;
    &:hover { transform: none; box-shadow: none; }
  `}

  /* A cancelled class stays on the timetable, struck through — it has to read as
     "this was called off", which an absent block cannot say. */
  opacity: ${props => (props.$cancelled ? 0.45 : 1)};
  text-decoration: ${props => (props.$cancelled ? 'line-through' : 'none')};
  border: ${props => (props.$flagged ? '2px solid #fff' : 'none')};
  outline: ${props => (props.$flagged ? `2px solid ${props.$color}` : 'none')};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    z-index: 100;
  }

  strong {
    display: block;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    display: block;
    opacity: 0.9;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Drag & drop ------------------------------------------------------------ */

export const DragGhost = styled.div`
  position: absolute;
  left: ${props => props.$left ?? '2px'};
  top: ${props => props.$top ?? '2px'};
  width: ${props => props.$width ?? 'calc(100% - 4px)'};
  height: ${props => props.$height ?? 'calc(100% - 4px)'};
  border-radius: 6px;
  border: 2px dashed ${props => props.$color};
  background: ${props => props.$color}22;
  pointer-events: none;
  z-index: 200;
  padding: 5px 7px;
  font-size: 11px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
`;

/** Grab edge for changing a class's length: bottom edge when time is vertical, right edge
    when it is horizontal. */
export const ResizeHandle = styled.div`
  position: absolute;
  ${props => (props.$horizontal
    ? `top: 0; bottom: 0; right: 0; width: 8px; cursor: ew-resize; border-radius: 0 6px 6px 0;`
    : `left: 0; right: 0; bottom: 0; height: 8px; cursor: ns-resize; border-radius: 0 0 6px 6px;`)}

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`;

export const ConflictMark = styled.span`
  position: absolute;
  top: 3px;
  right: 4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 800;
  line-height: 14px;
  text-align: center;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85);
`;

export const DropHint = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 8px;

  kbd {
    font-family: inherit;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 0 5px;
  }
`;

export const ScopeOption = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  margin-bottom: 8px;
  border: 1px solid ${props => (props.$primary ? BRAND : '#e5e7eb')};
  border-radius: 8px;
  background: ${props => (props.$primary ? '#fff7ed' : 'white')};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${BRAND};
    background: #fff7ed;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  strong {
    display: block;
    font-size: 14px;
    color: #1f2937;
    margin-bottom: 2px;
  }

  span {
    font-size: 12px;
    color: #6b7280;
  }
`;

export const ChangeSummary = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #374151;
  margin-bottom: 14px;

  div + div {
    margin-top: 3px;
  }

  b {
    color: #1f2937;
  }
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.28);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-right: 4px;
  text-decoration: none;
`;

// ==================== Week view ====================

export const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(150px, 1fr));
  gap: 1px;
  background: #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  min-width: 1050px;
`;

export const DayColumn = styled.div`
  background: white;
  min-height: 320px;
`;

export const DayHeader = styled.div`
  padding: 10px 8px;
  text-align: center;
  background: ${props => (props.$today ? BRAND : '#f9fafb')};
  color: ${props => (props.$today ? 'white' : '#374151')};
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid #e5e7eb;

  span {
    display: block;
    font-weight: 400;
    font-size: 11px;
    opacity: 0.8;
  }
`;

export const DayBody = styled.div`
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const RoomGroupLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 6px 0 1px;
`;

export const MiniCard = styled.div`
  border-left: 3px solid ${props => props.$color};
  background: #f9fafb;
  border-radius: 4px;
  padding: 5px 7px;
  font-size: 11px;
  cursor: pointer;
  opacity: ${props => (props.$cancelled ? 0.5 : 1)};
  text-decoration: ${props => (props.$cancelled ? 'line-through' : 'none')};

  &:hover {
    background: #f3f4f6;
  }

  strong {
    display: block;
    color: #1f2937;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    color: #6b7280;
  }
`;

// ==================== List view ====================

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    position: sticky;
    top: 0;
    background: #f9fafb;
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    color: #4b5563;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;

    &:hover {
      color: #1f2937;
    }
  }

  td {
    padding: 9px 12px;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
    vertical-align: top;
  }

  tbody tr {
    cursor: pointer;

    &:hover {
      background: #fafafa;
    }
  }

  /* A ten-column table on a phone is a sideways scroll nobody performs. Each class becomes a
     card instead, with the column header printed as the label of its own value — the same
     rows and the same order, folded rather than truncated. The data-label attribute comes
     from ListView's column definitions, so a column added there labels itself here. */
  ${media.phone} {
    font-size: 12px;

    thead {
      display: none;
    }

    tbody tr {
      display: block;
      margin-bottom: 8px;
      padding: 4px 0;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: white;
    }

    td {
      display: grid;
      grid-template-columns: 80px 1fr;
      align-items: baseline;
      gap: 10px;
      padding: 4px 12px;
      border-bottom: none;

      &::before {
        content: attr(data-label);
        font-size: 10px;
        font-weight: 700;
        color: #9ca3af;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
    }
  }
`;

export const Row = styled.tr`
  opacity: ${props => (props.$cancelled ? 0.55 : 1)};
  text-decoration: ${props => (props.$cancelled ? 'line-through' : 'none')};
`;

export const Pill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: white;
  background: ${props => props.$color || '#9ca3af'};
`;

export const ColorDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
  margin-right: 6px;
  vertical-align: middle;
`;

// ==================== Modal internals ====================

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;

  h2 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: #1f2937;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: #9ca3af;
  cursor: pointer;
  padding: 0 4px;

  &:hover {
    color: #4b5563;
  }
`;

export const ModalBody = styled.div`
  padding: 20px 22px;
  overflow-y: auto;
  flex: 1;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

export const Field = styled.div`
  margin-bottom: 14px;

  label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #4b5563;
    margin-bottom: 5px;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    color: #374151;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: ${BRAND};
    }
  }

  textarea {
    resize: vertical;
    min-height: 60px;
  }
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 2}, 1fr);
  gap: 12px;

  ${media.phone} {
    grid-template-columns: 1fr;
  }
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 18px;
  margin-bottom: 18px;

  ${media.phone} {
    grid-template-columns: 1fr;
  }
`;

export const InfoItem = styled.div`
  label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 2px;
  }

  div {
    font-size: 13px;
    color: #1f2937;
  }
`;

export const SecondaryButton = styled.button`
  padding: 9px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DangerButton = styled(SecondaryButton)`
  border-color: #fecaca;
  color: #b91c1c;

  &:hover:not(:disabled) {
    background: #fef2f2;
  }
`;

// ==================== Campus picker ====================
//
// A checkbox popover rather than a <select multiple>: choosing three of four campuses in a
// native multi-select means ctrl-clicking, and the closed state shows only one line. Here the
// trigger always says what is currently in view.

export const CampusPicker = styled.div`
  position: relative;
`;

export const CampusTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${props => (props.$open ? BRAND : '#d1d5db')};
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${BRAND};
  }

  span {
    color: #9ca3af;
    font-size: 10px;
  }
`;

export const CampusCount = styled.span`
  && {
    background: ${BRAND};
    color: white;
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 11px;
    font-weight: 600;
  }
`;

export const CampusMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  /* The picker is the last control in the toolbar, so a left-aligned menu would hang off
     the right edge of the page. */
  right: 0;
  z-index: 20;
  min-width: 220px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 6px;
`;

export const CampusOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f9fafb;
  }

  input {
    accent-color: ${BRAND};
    cursor: pointer;
    margin: 0;
  }
`;

export const CampusMenuFooter = styled.div`
  display: flex;
  gap: 6px;
  border-top: 1px solid #f3f4f6;
  margin-top: 4px;
  padding: 6px 4px 2px;

  button {
    flex: 1;
    padding: 6px 8px;
    border: none;
    border-radius: 5px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #e5e7eb;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
`;

/** Campus name on a room column header, when the grid spans more than one campus. */
export const ColumnCampus = styled.span`
  && {
    display: block;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.75;
  }
`;

// ==================== Scope choice (edit: this week / whole term) ====================
//
// Inline in the edit form rather than a follow-up dialog: unlike a drag — where the gesture
// is already finished and the question has to interrupt — here the user is still filling in
// fields, so the scope is just another thing they set before saving. Showing it up front also
// lets the form disable the fields the chosen scope cannot carry.

export const ScopeChoice = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 18px;
`;

export const ScopeChoiceButton = styled.button`
  text-align: left;
  padding: 10px 12px;
  border: 1px solid ${props => (props.$active ? BRAND : '#e5e7eb')};
  border-radius: 8px;
  background: ${props => (props.$active ? 'rgba(241, 175, 89, 0.12)' : 'white')};
  cursor: pointer;

  strong {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: ${props => (props.$active ? '#92400e' : '#374151')};
    margin-bottom: 2px;
  }

  span {
    display: block;
    font-size: 11px;
    line-height: 1.4;
    color: #6b7280;
  }

  &:hover {
    border-color: ${BRAND};
  }
`;

// ==================== Time-horizontal grid ====================
//
// The same grid rotated: lanes (rooms / teachers / days) stack as rows and time runs left to
// right. Worth having because the two orientations answer different questions — vertical
// packs many lanes across a wide screen and is the better "what is on right now"; horizontal
// gives each lane a full row and reads as a Gantt chart, which is the better "how busy is
// this room across the day" and stays legible with only a handful of lanes.

export const HGrid = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
`;

export const HInner = styled.div`
  display: grid;
  grid-template-columns: ${LANE_LABEL_WIDTH}px ${props => props.$trackWidth}px;
  min-width: min-content;
`;

export const HCorner = styled.div`
  position: sticky;
  left: 0;
  z-index: 4;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
`;

export const HTimeHeader = styled.div`
  position: relative;
  height: 30px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

export const HTimeTick = styled.div`
  position: absolute;
  top: 0;
  left: ${props => props.$left}px;
  height: 100%;
  padding-left: 5px;
  font-size: 11px;
  color: #6b7280;
  border-left: 1px solid #e5e7eb;
  line-height: 30px;
  white-space: nowrap;
`;

export const HLaneLabel = styled.div`
  position: sticky;
  left: 0;
  z-index: 3;
  background: ${BRAND};
  color: white;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: ${LANE_HEIGHT}px;

  span {
    font-weight: 400;
    font-size: 10px;
    opacity: 0.85;
  }
`;

export const HLaneCampus = styled.span`
  && {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.75;
  }
`;

export const HLaneTrack = styled.div`
  position: relative;
  min-height: ${LANE_HEIGHT}px;
  border-bottom: 1px solid #f3f4f6;
  background: ${props => (props.$isDropTarget ? 'rgba(241, 175, 89, 0.12)' : 'transparent')};
  cursor: ${props => (props.$creatable ? 'copy' : 'default')};
`;

export const HHourLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${props => props.$left}px;
  width: 1px;
  background: #f3f4f6;
  pointer-events: none;
`;
